"use client";

import { Dispatch, SetStateAction, useEffect, useState } from "react";
import styles from "./MatchCountdown.module.scss";

interface TimeLeft {
    hours: string;
    minutes: string;
    seconds: string;
}

const calculateTimeLeft = (
    matchTime: string,
    setTimerRunning: Dispatch<SetStateAction<boolean>>,
): TimeLeft => {
    // Parse IST time as UTC+5:30
    const istDate = new Date(matchTime);
    istDate.setMinutes(istDate.getMinutes() - 330); // Convert IST to UTC

    // Convert match time to the user's local timezone
    const matchLocalTime = new Date(istDate);

    // Get current local time
    const now = new Date();

    // Calculate remaining time in milliseconds
    const remainingMs = matchLocalTime.getTime() - now.getTime();

    if (remainingMs <= 0) {
        setTimerRunning(false);
        return { hours: "00", minutes: "00", seconds: "00" };
    }

    return {
        hours: String(Math.floor(remainingMs / (1000 * 60 * 60))).padStart(
            2,
            "0",
        ),
        minutes: String(
            Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60)),
        ).padStart(2, "0"),
        seconds: String(
            Math.floor((remainingMs % (1000 * 60)) / 1000),
        ).padStart(2, "0"),
    };
};

const MatchCountdown = ({
    matchTime,
    setTimerRunning,
    darkModeTextColor = "#fff",
}: {
    matchTime: string;
    setTimerRunning: Dispatch<SetStateAction<boolean>>;
    darkModeTextColor: string;
}) => {
    const [timeLeft, setTimeLeft] = useState<TimeLeft>(
        calculateTimeLeft(matchTime, setTimerRunning),
    );

    useEffect(() => {
        const interval = setInterval(() => {
            setTimeLeft(calculateTimeLeft(matchTime, setTimerRunning));
        }, 1000);

        return () => clearInterval(interval); // Cleanup on unmount
    }, [matchTime]);

    const theme = localStorage.getItem("theme");
    const darkThemeCustomStyle = {
        color: theme === "dark" ? darkModeTextColor : "",
    };

    return (
        <div className={styles.matchCountdownWrapper}>
            <div className={styles.unitTimeWrapper}>
                <div
                    className={styles.unitsLeft}
                    data-theme={theme}
                    style={darkThemeCustomStyle}
                >
                    {timeLeft.hours}
                </div>
                <div
                    className={styles.unitTimeLabel}
                    data-theme={theme}
                    style={darkThemeCustomStyle}
                >
                    Hours
                </div>
            </div>
            <div
                className={styles.unitSeperator}
                data-theme={theme}
                style={darkThemeCustomStyle}
            >
                :
            </div>
            <div className={styles.unitTimeWrapper}>
                <div
                    className={styles.unitsLeft}
                    data-theme={theme}
                    style={darkThemeCustomStyle}
                >
                    {timeLeft.minutes}
                </div>
                <div
                    className={styles.unitTimeLabel}
                    data-theme={theme}
                    style={darkThemeCustomStyle}
                >
                    Minutes
                </div>
            </div>
            <div
                className={styles.unitSeperator}
                data-theme={theme}
                style={darkThemeCustomStyle}
            >
                :
            </div>
            <div className={styles.unitTimeWrapper}>
                <div
                    className={styles.unitsLeft}
                    data-theme={theme}
                    style={darkThemeCustomStyle}
                >
                    {timeLeft.seconds}
                </div>
                <div
                    className={styles.unitTimeLabel}
                    data-theme={theme}
                    style={darkThemeCustomStyle}
                >
                    Seconds
                </div>
            </div>
        </div>
    );
};

export default MatchCountdown;
